import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDashTheme } from "../../hooks/useDashTheme";
import { useClassicOverview } from "../../hooks/useClassicOverview";
import { useClassicFilters, subjectPath, toolPath, filterQuery, classicPath } from "../../components/drilldown/drilldownRoutes";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";
import { fmtMins, fmtH } from "../../components/classic/micro";
import { SectionHead, GPanel } from "./parts";
import { CurvePanel, ToolsPanel, ProjectsPanelG, PeoplePanel } from "./Panels";
import { useGraphicalPeople } from "./useGraphicalPeople";
import { DrillFrame } from "./drill/DrillFrame";
import { KpiStrip } from "./drill/StoryChrome";
import { ZoomInstrument } from "./drill/ZoomInstrument";
import useEscapeBack from "./drill/useEscapeBack";

/** Hour-of-day → "HH:00", with 24 spoken as midnight so labels stay honest. */
const hourLabel = (h) => (h === 24 ? "midnight" : `${String(h).padStart(2, "0")}:00`);

/**
 * The day's shape as REAL time blocks. The overview for a one-day window
 * carries an hour curve — active minutes per hour of that day — and nothing
 * finer, so the segments are contiguous runs of non-zero hours, labeled at
 * hour precision and never sharper than the data. No usable hours → no
 * segments → the instrument hides itself.
 */
function hourRunSegments(curve) {
  if (!Array.isArray(curve) || !curve.some((v) => (Number(v) || 0) > 0)) return [];
  const runs = [];
  let run = null;
  for (let h = 0; h < 24; h += 1) {
    const v = Number(curve[h]) || 0;
    if (v > 0) {
      if (!run) run = { h0: h, h1: h, active: 0 };
      run.h1 = h;
      run.active += v;
    } else if (run) {
      runs.push(run);
      run = null;
    }
  }
  if (run) runs.push(run);
  return runs.map((r) => ({
    id: `hours-${r.h0}`,
    s: r.h0 * 60,
    e: (r.h1 + 1) * 60,
    ink: "blue",
    label: `Tracked activity ${hourLabel(r.h0)} to ${hourLabel(r.h1 + 1)} · ${fmtMins(r.active)} active`,
  }));
}

/**
 * DayDrilldown — layer 2 for a single day, opened from a rhythm bar.
 *
 * The mock's contract: a drilled page shows ONLY the drilled thing, half as
 * picture and half as key things, and everything on it drills further. This
 * page is the overview RE-READ for a one-day window — so the hour curve, the
 * tools, the projects and the people are all that day and nothing else. Each of
 * those still opens its own layer-2 page (tool / project / person), which is
 * how the day reaches layer 3.
 *
 * Real data throughout. The one honest gap: there is no per-hour person or tool
 * breakdown org-wide, so "who was active at 3pm" is not a panel here — the hour
 * curve answers "when", and the people/tools panels answer "who/what" for the
 * whole day.
 */
export default function DayDrilldown() {
  const t = useDashTheme();
  const navigate = useNavigate();
  const { date } = useParams();
  const { member } = useClassicFilters();

  // the window IS this one day — start and end both = date, local midnight span
  const range = React.useMemo(() => {
    const s = new Date(`${date}T00:00:00`);
    const e = new Date(`${date}T00:00:00`);
    if (Number.isNaN(s.getTime())) return null;
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);
    return { start: s, end: e, key: "custom" };
  }, [date]);

  const { overview, loading, error } = useClassicOverview(range, member);
  const people = useGraphicalPeople(range, member);

  const tot = overview?.totals;
  const tracked = tot?.trackedMinutes ?? 0;
  const active = tot?.activeMinutes ?? 0;
  const idle = tot?.idleMinutes ?? 0;
  const activePct = tracked > 0 ? Math.round((active / tracked) * 100) : 0;

  const segments = React.useMemo(() => hourRunSegments(overview?.hourCurve), [overview]);

  const backPath = classicPath(range, member);
  useEscapeBack(React.useCallback(() => navigate(backPath), [navigate, backPath]));

  const go = (path) => path && navigate(path);
  const openTool = (tool) => go(toolPath({ name: tool.name, type: tool.type }, range, member));
  const openProject = (p) => p?.projectId && go(subjectPath({ kind: "project", id: p.projectId, name: p.name }, range, member));
  const openPerson = (pp) => go(subjectPath({ kind: "person", id: pp.userId, name: pp.name }, range, member));

  const qs = filterQuery(range, member);
  const crumbs = [
    { label: "Dashboard", to: `${DASHBOARD_BASE}/graphical${qs ? `?${qs}` : ""}` },
    { label: date || "Day" },
  ];

  return (
    <DrillFrame
      t={t}
      title={date}
      subtitle={loading ? "loading…" : `${fmtH(tracked)} tracked · ${fmtMins(active)} active · ${fmtMins(idle)} idle · ${activePct}% active`}
      crumbs={crumbs}
      backTo={backPath}
      backLabel="Dashboard"
      subject={[date]}
      excludes={["every other day in the period"]}
      instrument={segments.length ? <ZoomInstrument segments={segments} viewport={null} focus={null} /> : null}
    >
      {error ? (
        <GPanel t={t}>
          <div style={{ padding: 26, textAlign: "center", color: t.c.textTertiary, fontSize: 13 }}>
            This day could not be loaded. {String(error)}
          </div>
        </GPanel>
      ) : (
        <>
          <div className="tgw-a tgw-a2">
            <KpiStrip t={t} items={[
              { label: "Tracked", value: fmtH(tracked), sub: date },
              { label: "Active", value: fmtMins(active), sub: `${activePct}% of the day` },
              { label: "Idle", value: fmtMins(idle), sub: "inside this day" },
              { label: "People", value: String(people.rows?.length || people.total || "—"), sub: "click anyone below" },
            ]} />
          </div>
          <div className="tgw-a tgw-a3">
            <SectionHead t={t} title="The picture" hint="what happened on this day" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 14, alignItems: "start" }}>
              <CurvePanel t={t} overview={overview} loading={loading} />
              <ToolsPanel t={t} overview={overview} loading={loading} onTool={openTool} />
            </div>
          </div>

          <div className="tgw-a tgw-a4">
            <SectionHead t={t} title="Key things" hint="click anyone or anything to go deeper" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 14, alignItems: "start" }}>
              <ProjectsPanelG t={t} overview={overview} loading={loading} onProject={openProject} />
              <PeoplePanel
                t={t}
                people={people.rows}
                total={people.total}
                loading={people.loading}
                page={people.page}
                pages={people.pages}
                onPage={people.setPage}
                pageSize={people.pageSize}
                onExport={people.exportPeople}
                onPerson={openPerson}
              />
            </div>
          </div>
        </>
      )}
    </DrillFrame>
  );
}
